import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { schema } from 'prose-core'
import { Fragment, NodeType, Slice } from 'prosemirror-model'
import { Plugin, PluginKey } from 'prosemirror-state'
import { dropPoint } from 'prosemirror-transform'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import { ourUuid } from 'utils'
import { uploadImageToCloudinary } from 'utils/cloudinary'
import { decodeBase64 } from 'lzutf8'
import Styles from './PasteImagePlugin.module.scss'

export const PasteImagePluginKey = new PluginKey('pasteImage')

const REMOVE_IMAGE_UPLOAD_KEY = 'RemoveImageUpload'
const ADD_IMAGE_UPLOAD_KEY = 'AddImageUpload'

interface UploadPlaceholder {
  id: string
  uploadPosition: number
}
interface State {
  readonly uploads: readonly UploadPlaceholder[]
}

export const PasteImagePlugin = (imageNode: NodeType) =>
  new Plugin<State>({
    key: PasteImagePluginKey,

    state: {
      init: (): State => {
        return { uploads: [] }
      },

      apply: (tr, oldState): State => {
        const removedImageKey = tr.getMeta(REMOVE_IMAGE_UPLOAD_KEY) as string | undefined
        const addedImage = tr.getMeta(ADD_IMAGE_UPLOAD_KEY) as UploadPlaceholder | undefined

        let newUploads = oldState.uploads

        // Remove resolved uploads
        if (removedImageKey) {
          newUploads = newUploads.filter((u) => u.id !== removedImageKey)
        }

        // Update uploads in progress positions
        if (tr.docChanged && newUploads.length) {
          // Update insert position if doc is changed while image is still processing
          newUploads = newUploads.map((upload) => {
            let mappedPos = upload.uploadPosition
            tr.steps.forEach((step) => {
              /**
               * Utils.ts->FixListAround() replaces the list with a new one, therefore corrupting the position of
               * the decorator (which serves as an anchor for the final position of the image once it
               * is uploaded). We therefore skip this mapping if we detect it.
               */
              if (!tr.getMeta('fixListAround')) {
                mappedPos = step.getMap().map(mappedPos)
              }
            })
            return {
              id: upload.id,
              uploadPosition: mappedPos,
            }
          })
        }

        // Add new uploads
        if (addedImage) {
          newUploads = [...newUploads, addedImage]
        }

        return { uploads: newUploads }
      },
    },

    props: {
      handlePaste: (view, event, slice) => handlePaste(view, event as ClipboardEvent, slice, imageNode),
      handleDOMEvents: {
        dragover: (view, event) => {
          event.preventDefault()
          return true
        },
        drop: (view, event) => handleDrop(view, event as DragEvent, imageNode),
      },
      decorations: (state) => {
        const pluginState = PasteImagePluginKey.getState(state) as State | undefined

        if (pluginState && pluginState.uploads.length) {
          const decos: Decoration[] = []
          for (const upload of pluginState.uploads) {
            const loaderContainerNode = document.createElement('div')
            const loaderNode = document.createElement('div')
            loaderContainerNode.className = Styles.loaderContainer
            loaderNode.className = Styles.loader

            loaderContainerNode.appendChild(loaderNode)

            decos.push(Decoration.widget(upload.uploadPosition, loaderContainerNode))
          }
          return DecorationSet.create(state.doc, decos)
        }

        return
      },
    },
  })

const handlePaste = (view: EditorView, event: ClipboardEvent, slice: Slice, imageNode: NodeType) => {
  let items = event.clipboardData?.items

  if (!items) return true

  let imageBlob: File | undefined

  /**
   * Microsoft Word, as well as clipboard managers, will sometimes insert multiple representations
   * into the clipboard, i.e. an image + text/html
   *
   * We should prioritize the text/html representation in such cases, unless the content is only an image
   *
   * Workaround for this issue: https://linear.app/almanac/issue/EDI-51/pasting-from-word-creates-an-image
   */
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const sliceOnlyContainsImage = slice.content.childCount === 1 && slice.content.firstChild!.type === schema.nodes.image
  if (sliceOnlyContainsImage) {
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    imageBlob = getDataUriImage(slice.content.firstChild!.attrs.src)
  } else if (Array.from(items).some((item) => item.type === 'text/html')) {
    return false
  }

  if (!imageBlob) {
    imageBlob = getImageBlob(items)
  }

  if (imageBlob) {
    const tr = view.state.tr
    const placeholderKey = ourUuid()
    tr.setMeta(ADD_IMAGE_UPLOAD_KEY, {
      id: placeholderKey,
      uploadPosition: view.state.selection.from,
    } as UploadPlaceholder)
    /* Normally, Prose Mirror sets this itself, but when we dispatch the tr inside of a
    a plugin's handlePaste, the meta isn't set properly*/
    tr.setMeta('paste', true)

    view.dispatch(tr)

    uploadAndInsertImage(view, imageBlob, placeholderKey, imageNode)

    return true
  }

  return false
}

const handleDrop = (view: EditorView, event: DragEvent, imageNode: NodeType) => {
  let items = event.dataTransfer?.items

  if (!items) return true

  /**
   * Microsoft Word, as well as clipboard managers, will sometimes insert multiple representations
   * into the clipboard, i.e. an image + text/html
   *
   * We should prioritize the text/html representation in such cases
   *
   * Workaround for this issue: https://linear.app/almanac/issue/EDI-51/pasting-from-word-creates-an-image
   */
  if (Array.from(items).some((item) => item.type === 'text/html')) {
    return false
  }

  const imageBlob = getImageBlob(items)

  if (imageBlob) {
    let dropPosition = view.posAtCoords({ left: event.clientX, top: event.clientY })

    if (!dropPosition) return false

    event.preventDefault()
    event.stopPropagation()

    // temporary node to test dropping point
    const node = imageNode.create({ src: '' })
    let target = dropPoint(view.state.doc, dropPosition.pos, new Slice(Fragment.from(node), 0, 0)) || dropPosition.pos
    const tr = view.state.tr
    const placeholderKey = ourUuid()
    tr.setMeta(ADD_IMAGE_UPLOAD_KEY, { id: placeholderKey, uploadPosition: target } as UploadPlaceholder)
    view.dispatch(tr)

    uploadAndInsertImage(view, imageBlob, placeholderKey, imageNode)

    return true
  }

  return false
}

const uploadAndInsertImage = (view: EditorView, image: File, placeholderKey: string, imageNode: NodeType) => {
  readAndUpload(
    image,
    (secureUrl) => {
      const { state } = view
      const { tr } = state
      const pluginState = PasteImagePluginKey.getState(state) as State

      const placeholder = pluginState.uploads.find((upload) => upload.id === placeholderKey)

      if (secureUrl && placeholder) {
        const node = imageNode.createAndFill({ src: secureUrl })
        if (node) {
          const $uploadPosition = state.doc.resolve(placeholder.uploadPosition)
          tr.insert(placeholder.uploadPosition, node)
          if ($uploadPosition.node().type.groups.includes('textWrap') && !$uploadPosition.node().childCount)
            tr.delete($uploadPosition.before(), $uploadPosition.after())
        }
      } else {
        showErrorMessageToast('Something went wrong inserting this image, please try again later')
      }
      tr.setMeta(REMOVE_IMAGE_UPLOAD_KEY, placeholderKey)
      try {
        view.dispatch(tr)
      } catch (e) {}
    },
    () => {
      showErrorMessageToast('There was an error processing this image, please try again later')
      const { tr } = view.state
      tr.setMeta(REMOVE_IMAGE_UPLOAD_KEY, placeholderKey)
      view.dispatch(tr)
    },
  )
}

const readAndUpload = (image: File, onSuccess: (secureUrl: string) => void, onError: () => void) => {
  const reader = new FileReader()

  reader.onload = (event) => {
    if (event.target?.result) {
      uploadImageToCloudinary(event.target?.result as string).then((e) => {
        e?.secure_url ? onSuccess(e.secure_url) : onError()
      })
    } else {
      // Error reading file
      onError()
    }
  }

  reader.readAsDataURL(image)
}

const getImageBlob = (items: DataTransferItemList) => {
  const image = Array.from(items).find((item) => item.type.startsWith('image') && item.kind === 'file')
  if (image == null) return
  return image.getAsFile() ?? undefined
}

const getDataUriImage = (dataUri: any): File | undefined => {
  try {
    if (typeof dataUri !== 'string' || !dataUri.startsWith('data:image')) return
    const parts = dataUri.split(/[,;]/)
    if (parts.length !== 3 || parts[1] !== 'base64') return
    const type = parts[0].slice(5).toLowerCase()
    if (!['image/png', 'image/jpeg', 'image/gif', 'image/webp'].includes(type)) return
    const buffer = decodeBase64(parts[2])
    const arrayBuffer = buffer.buffer.slice(buffer.byteOffset, buffer.byteOffset + buffer.byteLength) as ArrayBuffer
    return new File([arrayBuffer], '', { type })
  } catch {}
}
